// DEPENDENCIES
import { FormikProps, FormikValues } from "formik";
import * as Sentry from "@sentry/react";

// TYPES
import { AppDispatch, RootState, store } from "../../app/store";
import {
  ComponentStep,
  MidgardQuestion,
  MidgardQuestions,
  ShareholderData,
  SignatoryData,
  StepOnePJ,
  StepTwoPJ,
  StepThreePJ,
  StepFourPJ,
  StepFivePJ,
} from "../../types";

// UTILS
import {
  getBusinessQuestions,
  getDraft,
  getDrafts,
  getLastAllariaAccount,
  validateAfip,
} from "../../connectors/connectors";
import {
  goToStep,
  isDev,
  parseBirthDate,
  parseGenderToInitial,
  parseTelephone,
  buildFullAddress,
} from "../../app/utils";
import { emptyPhysicalShareholder } from "../../constants";
import {
  checkDifferentTypePendingDraft,
  convertBoolean,
  getIdentityResumePoint,
  getReferrerToSend,
  getResumeReferralValues,
  isAllariaFondosOnboarding,
  loadRemoteIdentityProgress,
  parseStateAddress,
  promptRemoteIdentityResume,
  roundPercentage,
  setFormValues,
} from "../../utils";
import { getCompanyData, getPartnership } from "../../utils/companies";
import {
  onUpdateInfoStep,
  onUpdatePendingOnboardingType,
} from "../../reducers/infoStepReducer";
import {
  addSendedStep,
  onUpdateDraftUUID,
  onUpdateFormData,
  onUpdateHasExternalInformation,
  onUpdateStep,
} from "../../reducers/onboardingDataReducer";
import {
  normalizeInvestmentProfileForApi,
  parseQualifiedInvestorFromDraft,
} from "../shared/qualifiedInvestorField";

// STEPS
import emailValidationStep from "../physical/emailValidationStep";
import { AxiosError } from "axios";

export const getQuestions = async (): Promise<MidgardQuestions> => {
  try {
    const { data } = await getBusinessQuestions({
      company: getCompanyData().companyName,
    });
    return data;
  } catch (error) {
    throw new Error("Error al obtener las preguntas");
  }
};

export const getEmailVerificationHandlers = (
  dispatch: AppDispatch,
  state: RootState,
  formik: FormikProps<FormikValues>,
) => {
  const completeSteps = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    steps: any,
    isCheckingESCO?: boolean,
    //The draft's current one, which the back checks the steps against
    draftReferrer?: string | null,
  ) => {
    const isActiveAccount = isCheckingESCO; //Has information in ESCO
    //Do not override representative's cuit, gender and identityDocument when is ESCO data in dev, to prevent failing validations
    const overrideRepresentativeData = !(isCheckingESCO && isDev);

    //isCheckingESCO means is checking getDrafts endpoint
    const stepOne = steps.find(
      (step: StepOnePJ) => step["@type"] === "1_JURIDICAL",
    );

    let stepOneParsed = {
      ...stepOne,
      referrer: null,
      representativeInformation: null,
      validations: null,
      ...(stepOne &&
        getResumeReferralValues(
          isActiveAccount ? null : (draftReferrer ?? stepOne.referrer),
          formik?.values,
        )),
    };

    const parsePep = (pep: string | undefined) => {
      if (pep === null || pep === undefined) {
        if (isActiveAccount)
          return ""; //Esco doesn't give us specific information about pep, so return empty string
        else return "NONE";
      } else return pep;
    };

    if (stepOne) {
      if (stepOne.representativeInformation) {
        const {
          name,
          lastName,
          addresses,
          civilStatus,
          identityDocument,
          birthDate,
          gender,
          spouse,
          cuit,
          citizenship,
          telephone,
          activities,
          activitiesDetails,
          pep,
          pepDetails,
          birthCountry,
        } = stepOne.representativeInformation;

        const representativeInformationRest = {
          ...stepOne.representativeInformation,
        };
        delete representativeInformationRest.cuit;
        delete representativeInformationRest.gender;
        delete representativeInformationRest.identityDocument;

        let parsedAddresses = {};
        if (addresses?.[0]?.street && addresses[0].city) {
          const { street, number, city, municipality, ...restOfAddress } =
            addresses[0];
          parsedAddresses = {
            ...restOfAddress,
            fullAddress: buildFullAddress(street, number, city, municipality),
            municipality,
            city,
            street,
            number,
          };
        }

        //Parse birthDate
        let birthDateParsed = "";
        if (birthDate) {
          birthDateParsed = parseBirthDate(birthDate);
        }

        let identityDocumentParsed = {};
        if (identityDocument) {
          identityDocumentParsed = {
            identityDocument: identityDocument.idNumber,
            tramitNumber: identityDocument.procedureId,
          };
        }

        let genderParsed = "";
        if (gender) {
          genderParsed = parseGenderToInitial(gender);
        }

        // Parse telephone to prevent bad formatting
        let telephoneParsed = "";
        if (telephone) telephoneParsed = parseTelephone(telephone);

        if (spouse) {
          const { name, lastName, cuit } = spouse || {};
          stepOneParsed.spouseName = name;
          stepOneParsed.spouseLastName = lastName;
          stepOneParsed.spouseCuil = cuit;
        }

        stepOneParsed = {
          ...representativeInformationRest,
          ...stepOneParsed,
          birthCountry,
          name: name,
          lastName,
          civilStatus: civilStatus,
          citizenship: citizenship,
          representativesAddresses: parsedAddresses,
          pep: parsePep(pep),
          pepDetails,
          birthDate: birthDateParsed,
          telephone: isActiveAccount ? "" : telephoneParsed,
          activities: isActiveAccount ? null : activities,
          activitiesDetails: isActiveAccount ? "" : activitiesDetails,
          representativeInformation: {
            ...representativeInformationRest,
            ...(overrideRepresentativeData && {
              cuit,
              gender,
              identityDocument,
            }),
          },
          ...(overrideRepresentativeData && {
            cuit: cuit || formik?.values?.cuit,
            gender: genderParsed,
            ...identityDocumentParsed,
          }),
        };
      }
    }

    //Parse address

    //STEP TWO
    let stepTwoParsed;
    const stepTwo = steps.find(
      (step: StepTwoPJ) => step["@type"] === "2_JURIDICAL",
    );

    if (stepTwo) {
      const { businessInformation } = stepTwo;

      const {
        hasJointSignature,
        isComplexStructure,
        isInEconomicGroup,
        isPublicEntity,
        registrationDate,
        registrationProvince,
        bankAccounts,
        foreignTaxDetail,
      } = businessInformation;

      let entityAddressParsed = {};
      if (
        businessInformation.addresses?.[0]?.street &&
        businessInformation.addresses[0].city
      ) {
        const {
          zipCode,
          state,
          city,
          street,
          number,
          floor,
          apartment,
          country,
          municipality,
        } = businessInformation.addresses[0];

        entityAddressParsed = {
          zipCode,
          state,
          municipality,
          city,
          street,
          number,
          fullAddress: buildFullAddress(street, number, city),
          floor,
          apartment,
          country,
        };
      }

      const foreignTaxDetailParsed = {
        idNumber: foreignTaxDetail?.idNumber,
        country: foreignTaxDetail?.address?.country,
        state: foreignTaxDetail?.address?.state,
        fullAddress: buildFullAddress(
          foreignTaxDetail?.address?.street,
          foreignTaxDetail?.address?.number,
          foreignTaxDetail?.address?.city,
        ),
        street: foreignTaxDetail?.address?.street,
        number: foreignTaxDetail?.address?.number,
        city: foreignTaxDetail?.address?.city,
        municipality: foreignTaxDetail?.address?.city,
        zipCode: foreignTaxDetail?.address?.zipCode,
        floor: foreignTaxDetail?.address?.floor,
        apartment: foreignTaxDetail?.address?.apartment,
      };

      stepTwoParsed = {
        entityAddress: entityAddressParsed,
        businessInformation: {
          ...businessInformation,
          activitiesDetails: "",
          bankAccounts:
            bankAccounts && bankAccounts.length > 0
              ? bankAccounts[0].cxu
              : null,
          bankAccountBool:
            bankAccounts && bankAccounts.length > 0 ? "SI" : "NO",
          registrationProvince: parseStateAddress(registrationProvince),
          registrationDate: isActiveAccount ? null : registrationDate,
          hasJointSignature: convertBoolean(hasJointSignature),
          isComplexStructure: convertBoolean(isComplexStructure),
          isInEconomicGroup: convertBoolean(isInEconomicGroup),
          isPublicEntity: convertBoolean(isPublicEntity),
          foreignTaxDetailBool: foreignTaxDetail ? "SI" : "NO",
          foreignTaxDetail: foreignTaxDetailParsed ?? null,
        },
      };
    }

    let stepThreeParsed;

    //STEP THREE
    const stepThree = steps.find(
      (step: StepThreePJ) => step["@type"] === "3_JURIDICAL",
    );

    if (stepThree && stepThree.additionalSignatories.length > 0) {
      const signatories = stepThree.additionalSignatories.map(
        (signatory: SignatoryData) => {
          const signatoryWithConfirmMail = {
            ...signatory,
            confirmEmail: signatory.email,
            idNumber: signatory.idNumber,
          };
          return signatoryWithConfirmMail;
        },
      );
      //Add original signatory to the list
      const originalSignatory = {
        name: stepOne.representativeInformation.name,
        lastName: stepOne.representativeInformation.lastName,
        email: stepOne.representativeInformation.email,
        idNumber: stepOne.representativeInformation.identityDocument.idNumber,
      };

      const allSignatories = [originalSignatory, ...signatories];
      stepThreeParsed = {
        signatories: allSignatories,
      };
    }

    //STEP FOUR
    const stepFour = steps.find(
      (step: StepFourPJ) => step["@type"] === "4_JURIDICAL",
    );
    let stepFourParsed;

    if (stepFour) {
      const { shareholders } = stepFour;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const parseShareholder = (shareholder: any) => {
        const { pep, pepDetails, address, foreignTaxDetail, spouse } =
          shareholder;
        let addressParsed;

        let shareholderParsed = {
          ...shareholder,
          ownership: roundPercentage(shareholder.ownership),
        };
        if (shareholder.personType === "PHYSICAL") {
          const {
            state,
            street,
            number,
            city,
            apartment,
            floor,
            country,
            zipCode,
          } = address ?? {};
          addressParsed =
            street && city
              ? {
                  state: parseStateAddress(state),
                  municipality: city,
                  fullAddress: buildFullAddress(street, number, city),
                  floor,
                  apartment,
                  country,
                  street,
                  city,
                  zipCode,
                }
              : emptyPhysicalShareholder.address;
          const spouseParsed = {
            spouseName: "",
            spouseLastName: "",
            spouseCuil: "",
          };
          if (spouse) {
            const { name, lastName, cuit } = spouse || {};
            spouseParsed.spouseName = name || "";
            spouseParsed.spouseLastName = lastName || "";
            spouseParsed.spouseCuil = cuit || "";
          } else delete shareholderParsed.spouse;

          shareholderParsed = {
            "@type": "PHYSICAL",
            ...shareholderParsed,
            pep: parsePep(pep),
            address: addressParsed,
            ...(pepDetails && { pepDetails: pepDetails ? pepDetails : "" }),
            ...spouseParsed,
          };
        } else
          shareholderParsed = { ...shareholderParsed, "@type": "JURIDICAL" };

        const foreignTaxDetailBool = foreignTaxDetail !== null ? "SI" : "NO";

        const foreignTaxDetailParsed = {
          idNumber: foreignTaxDetail?.idNumber,
          address: {
            country: foreignTaxDetail?.address?.country,
            state: foreignTaxDetail?.address?.state,
            fullAddress: buildFullAddress(
              foreignTaxDetail?.address?.street,
              foreignTaxDetail?.address?.number,
              foreignTaxDetail?.address?.city,
            ),
            street: foreignTaxDetail?.address?.street,
            number: foreignTaxDetail?.address?.number,
            city: foreignTaxDetail?.address?.city,
            municipality: foreignTaxDetail?.address?.city,
            zipCode: foreignTaxDetail?.address?.zipCode,
            floor: foreignTaxDetail?.address?.floor,
            apartment: foreignTaxDetail?.address?.apartment,
          },
        };

        return {
          ...shareholderParsed,
          foreignTaxDetailBool,
          foreignTaxDetail:
            foreignTaxDetailBool === "SI"
              ? foreignTaxDetailParsed
              : {
                  idNumber: "",
                  address: {
                    country: "",
                    fullAddress: "",
                    street: "",
                    city: "",
                    state: "",
                    municipality: "",
                    zipCode: "",
                  },
                },
        };
      };

      const parsedShareholders = shareholders.map(parseShareholder);

      stepFourParsed = {
        ...stepFour,
        shareholders: parsedShareholders,
      };
    }

    //STEP FIVE
    const stepFive = steps.find(
      (step: StepFivePJ) => step["@type"] === "5_JURIDICAL",
    );
    let stepFiveParsed;
    if (stepFive) {
      const questions = await getQuestions();

      const answers: string[] = stepFive?.investmentProfile?.choices || [];

      const investmentProfileParsed = () => {
        const result: Record<string, string> = {};
        questions.forEach((question) => {
          const res = findAnswerAboutQuestion(question);
          if (res) {
            result[question.externalCode] = res.option;
          }
        });
        return result;
      };

      const findAnswerAboutQuestion = (question: MidgardQuestion) =>
        question.choices.find((choice) =>
          answers.find((answer) => answer === choice.externalCode),
        );

      if (stepFive) {
        stepFiveParsed = {
          investmentProfile: investmentProfileParsed(),
          ...parseQualifiedInvestorFromDraft(stepFive.qualifiedInvestorType),
        };
      }
    }

    const newValues = {
      ...stepOneParsed,
      ...stepTwoParsed,
      ...stepThreeParsed,
      ...stepFourParsed,
      ...stepFiveParsed,
    };

    setFormValues(formik, newValues);

    if (stepOne) {
      dispatch(
        addSendedStep(
          isActiveAccount
            ? stepOne
            : { ...stepOne, referrer: getReferrerToSend(stepOneParsed) },
        ),
      );
      dispatch(onUpdateFormData(newValues));
    }
    if (!isActiveAccount) {
      if (stepTwo) dispatch(addSendedStep(stepTwo));
      if (stepThree) dispatch(addSendedStep(stepThree));
      if (stepFour) {
        const shareholdersWithType = stepFour.shareholders.map(
          (shareholder: ShareholderData) => {
            return { ...shareholder, "@type": shareholder?.personType };
          },
        );
        const stepFourToStore = {
          "@type": "4_JURIDICAL",
          shareholders: shareholdersWithType,
        };
        dispatch(addSendedStep(stepFourToStore));
      }
      if (stepFive)
        dispatch(addSendedStep(normalizeInvestmentProfileForApi(stepFive)));
    }

    return { stepOneParsed, stepTwoParsed, stepOne, stepTwo };
  };

  const onAuthorize = async (draftUUID?: string) => {
    try {
      if (!draftUUID || !state || !formik)
        throw new Error("DraftUUID or state not found");
      const { setFieldValue, values } = formik;

      dispatch(onUpdateDraftUUID(draftUUID));

      const remoteDraft = await getDraft({ draftUUID })
        .then(({ data }) => data)
        .catch((error) => {
          console.log("Error on getDraft", error);
          return null;
        });

      if (remoteDraft?.referrer) {
        const url = new URL(window.location.href);
        url.searchParams.set("ref", remoteDraft.referrer);
        window.history.replaceState(window.history.state, "", url);
      }

      const cuit =
        values.businessInformation?.cuit || remoteDraft?.juridicalPersonCuit;
      const email = values.email || remoteDraft?.email;

      if (cuit && !values.businessInformation?.cuit) {
        setFieldValue("businessInformation.cuit", cuit);
        dispatch(
          onUpdateFormData({
            businessInformation: { ...values.businessInformation, cuit },
          }),
        );
      }
      if (email && !values.email) {
        setFieldValue("email", email);
        dispatch(onUpdateFormData({ email }));
      }

      //Allaria Fondos only shows the data we already have and ends the flow
      let showActiveAccountSummary = false;

      if (
        isAllariaFondosOnboarding() &&
        !getPartnership(values)?.skipActiveAccountSummary
      ) {
        try {
          //An existing Allaria account ("comitente") is what sends the user
          //to the summary, ESCO is not checked. The summary and the
          //spreadsheet are filled from it
          const { data } = await getLastAllariaAccount({ cuit });

          if (data?.accountId) {
            setFieldValue("allariaAccount", data);
            //infoStep is persisted, so the account has to survive a reload too
            dispatch(onUpdateFormData({ allariaAccount: data }));
            showActiveAccountSummary = true;
            dispatch(onUpdateInfoStep("ACTIVE_ACCOUNT_SUMMARY"));
            dispatch(onUpdateHasExternalInformation(true));
          }
        } catch (error) {
          //Does not block the flow: without an account it goes on as a new
          //onboarding
          console.log(error, "Error on getLastAllariaAccount");

          //The status goes as a tag so the 404s (the expected answer
          //when the company has no Allaria account) can be filtered out
          //without opening every event, and the body as an extra: it is
          //what tells that 404 apart from the route not being deployed,
          //which answers with Spring's own "Not Found" payload
          const response =
            error instanceof AxiosError ? error.response : undefined;

          Sentry.captureException(error, {
            tags: {
              flow: "cuotapartista_request",
              "account.status": response?.status ?? "unknown",
            },
            extra: { cuit, body: response?.data },
          });
        }
      }

      //The summary only uses the Allaria account data
      if (!showActiveAccountSummary) {
        try {
          // Check AFIP data only for autocomplete
          // Do not send in validations
          // If it fails, it will not stop the process
          const afipResponse = await validateAfip({
            cuit,
            company: getCompanyData().companyName,
          });

          if (afipResponse.data.status === "OK") {
            const legalName =
              afipResponse.data.validations[0].information.datosGenerales
                .razonSocial;

            setFieldValue("businessInformation.legalName", legalName);
          }
        } catch (error) {
          console.log(error, "Error on validate AFIP");
          Sentry.captureException(error);
        }
      }

      if (!isAllariaFondosOnboarding()) {
        try {
          //Check if there is a draft with the same CUIT
          const draftsResponse = await getDrafts({
            cuit,
            personType: "JURIDICAL",
          });

          const stepsDrafts = draftsResponse.data.steps;
          if (stepsDrafts && stepsDrafts.length > 0) {
            await completeSteps(stepsDrafts, true);
            dispatch(onUpdateInfoStep("ACTIVE_ACCOUNT"));
            dispatch(onUpdateHasExternalInformation(true));
          }
        } catch (error) {
          console.log(error, "Error on getDrafts");
          throw new Error("FAILED GET ESCO DATA");
        }
      }
      if (
        remoteDraft &&
        state.infoStep.type !== "ACTIVE_ACCOUNT" &&
        !showActiveAccountSummary
      ) {
        try {
          const steps = remoteDraft.steps;
          const otherTypeDraft = checkDifferentTypePendingDraft(remoteDraft);

          if (!otherTypeDraft)
            await loadRemoteIdentityProgress(
              dispatch,
              remoteDraft,
              formik,
              "representativesAddresses",
            );

          if (steps && steps.length > 0) {
            if (otherTypeDraft) {
              dispatch(onUpdatePendingOnboardingType(otherTypeDraft));
              dispatch(onUpdateInfoStep("ONBOARDING_IN_PROGRESS"));
            } else {
              dispatch(onUpdateInfoStep("PENDING_REGISTRY_REMOTE"));
              await completeSteps(steps, false, remoteDraft.referrer);
            }
          } else if (!otherTypeDraft) {
            promptRemoteIdentityResume(dispatch);
          }
        } catch (error) {
          console.log("Error on remote draft", error);
        }
      }

      if (remoteDraft?.referrer && !checkDifferentTypePendingDraft(remoteDraft)) {
        const referralValues = getResumeReferralValues(
          remoteDraft.referrer,
          values,
        );
        //Field by field, or it would drop the allariaAccount set above
        setFormValues(formik, referralValues);
        dispatch(onUpdateFormData(referralValues));
      }
    } catch (error) {
      console.log(error, "Error on authorize");
      Sentry.captureException(error);
    }
  };

  const onSuccess = async () => {
    const { type } = store.getState().infoStep;

    if (type !== null) {
      dispatch(onUpdateStep("information-step"));
    } else {
      const resumePoint = getIdentityResumePoint(dispatch, 3);
      goToStep({
        dispatch,
        step: resumePoint.step,
        substep: resumePoint.substep,
      });
    }
  };

  return { onAuthorize, onSuccess, completeSteps };
};

const emailValidationJuridicalStep = ({
  step,
}: {
  step: number;
}): ComponentStep => ({
  ...emailValidationStep({
    step,
    onAuthorize: (draftUUID, dispatch, formik, state) =>
      getEmailVerificationHandlers(dispatch, state, formik).onAuthorize(
        draftUUID,
      ),
    onSuccess: (dispatch, state, formik) =>
      getEmailVerificationHandlers(dispatch, state, formik).onSuccess(),
  }),
  step,
});

export default emailValidationJuridicalStep;
